import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2, Video, ArrowLeft, FileText } from 'lucide-react';
import Timer from '../components/Timer';
import PdfViewer from '../components/PdfViewer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminSessaoPage = () => {
  const { sessaoId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sessao, setSessao] = useState(null);
  const [estacao, setEstacao] = useState(null);
  const [roteiro, setRoteiro] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSessaoData();
  }, [sessaoId]);

  const fetchSessaoData = async () => {
    try {
      // Fetch sessao
      const sessaoResponse = await axios.get(`${API}/sessoes/${sessaoId}`);
      setSessao(sessaoResponse.data);

      // Fetch estacao
      const estacoesResponse = await axios.get(`${API}/estacoes`);
      const estacaoData = estacoesResponse.data.find(e => e.id === sessaoResponse.data.estacaoId);
      setEstacao(estacaoData);

      // Determine papel do usuário atual
      const papel = sessaoResponse.data.criadorId === user.id 
        ? sessaoResponse.data.papelCriador 
        : sessaoResponse.data.papelConvidado;

      // Fetch roteiro PDF baseado no papel
      const roteiroPdfResponse = await axios.get(`${API}/roteiros-pdf?estacaoId=${sessaoResponse.data.estacaoId}&tipo=${papel}`);
      if (roteiroPdfResponse.data.length > 0) {
        setRoteiro(roteiroPdfResponse.data[0]);
      }
    } catch (error) {
      toast.error('Erro ao carregar dados da sessão');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleVoltar = () => {
    navigate('/admin/sessoes/espera');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#191919]">
        <div className="text-white text-xl flex items-center gap-2">
          <Loader2 className="w-6 h-6 animate-spin" />
          Carregando sessão...
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#191919] text-white">
      <div className="border-b border-gray-800">
        <div className="container mx-auto px-4 py-4">
          <Button
            onClick={handleVoltar}
            variant="ghost"
            className="text-gray-400 hover:text-white mb-2"
            data-testid="voltar-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{estacao?.nome}</h1>
            <p className="text-gray-400 text-sm mt-1">{estacao?.descricaoCurta}</p>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Video Area */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="bg-[#0f0f0f] border-gray-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Video className="w-5 h-5 text-[#CF0D1D]" />
                  Chamada de Vídeo
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div 
                  className="aspect-video bg-black rounded-lg flex flex-col items-center justify-center border-2 border-dashed border-gray-700"
                  data-testid="video-placeholder"
                >
                  <Video className="w-16 h-16 text-gray-600 mb-4" />
                  <p className="text-xl font-semibold text-white mb-2">Sessão em Andamento</p>
                  <p className="text-sm text-gray-400">A integração de vídeo será implementada em breve</p>
                </div>
              </CardContent>
            </Card>

            {/* Timers */}
            {estacao && (
              <Timer 
                sessaoId={sessaoId} 
                tempoMaximoMinutos={estacao.tempoMaximoMinutos}
                isAdmin={true}
              />
            )}
          </div>

          {/* Roteiro Area */}
          <div className="lg:col-span-1">
            <Card className="bg-white border-gray-200 h-full">
              <CardHeader>
                <CardTitle className="text-[#191919] flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#CF0D1D]" />
                  Seu Roteiro (Ator)
                </CardTitle>
              </CardHeader>
              <CardContent>
                {roteiro ? (
                  <div className="space-y-4">
                    <div
                      className="border border-gray-300 rounded-lg bg-gray-50"
                      style={{ maxHeight: '75vh', overflowY: 'auto', overflowX: 'hidden', padding: 8 }}
                    >
                      <PdfViewer
                        url={`${BACKEND_URL}${roteiro.arquivoUrl}`}
                        title="Roteiro PDF"
                      />
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <p className="text-xs text-blue-800">
                        <strong>Dica:</strong> Interprete o papel conforme o roteiro e avalie o desempenho do médico.
                      </p>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">Nenhum roteiro disponível para esta estação.</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Session Info */}
        <div className="mt-6">
          <Card className="bg-[#0f0f0f] border-gray-800">
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-gray-400 text-sm mb-1">Status da Sessão</p>
                  <div className="inline-flex items-center gap-2 px-3 py-1 bg-green-500/20 rounded-full">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <p className="text-green-500 font-semibold text-sm uppercase">
                      {sessao?.status.replace('_', ' ')}
                    </p>
                  </div>
                </div>
                <div>
                  <p className="text-gray-400 text-sm mb-1">Tempo Máximo</p>
                  <p className="text-white font-semibold text-lg">{estacao?.tempoMaximoMinutos} minutos</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm mb-1">Início da Chamada</p>
                  <p className="text-white font-semibold text-lg">
                    {sessao?.inicioChamada 
                      ? new Date(sessao.inicioChamada).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
                      : '-'
                    }
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminSessaoPage;